import React, { useEffect, useState } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'stores';
// icons
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { BiCalendarCheck } from 'react-icons/bi';
// api
// constants
import { choicesUserType } from 'constants';
// components
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import { SelectInputCustom } from 'components/input/index';
import CustomInput from 'components/backoffice/customInput';
import ChangePassword from 'components/backoffice/modals/ChangePassword';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import SearchCompanyInput from 'components/backoffice/customInput/SearchCompanyInput';
import DatePicker from 'components/backoffice/dateTimePicker';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const UserForm = ({
  isEditing,
  onSubmitForm,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  id,
  backendErrors,
  loading
}) => {
  const userPermissions = usePermissions();
  const { t } = useTranslation(['backoffice']);
  const [showPassword, setShowPassword] = useState(false);
  const [modalChangePassword, setModalChangePassword] = useState(false);
  const { isWorker } = useUserStore();

  const {
    formState: { errors },
    setValue,
    reset,
    control,
    handleSubmit,
    watch,
    setError
  } = useForm();

  useEffect(() => {
    if (backendErrors) {
      Object.entries(backendErrors).forEach(([field, message]) => {
        setError(field, { type: 'manual', message });
      });
    }

  }, [backendErrors, errors]);

  const choices = choicesUserType(t);
  const typeValue = watch('profile');

  useEffect(() => {
    if (!isEditing)
      reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).map((item) => {
        setValue(item[0], item[1])
      })
    }
  }, [defaultValues])

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="first_name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('nombre')}
                  onChange={(e) => onChange(e)}
                  placeholder={t('nombre')}
                />
              )}
            />
            {errors?.first_name && <small className="text-danger ">{errors.first_name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  value={value}
                  label={t('last_name')}
                  onChange={(e) => onChange(e)}
                  placeholder={t('last_name')}
                />
              )}
            />
            {errors?.last_name && <small className="text-danger ">{errors.last_name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="email"
                  value={value}
                  label={t('correoelectronico')}
                  placeholder={t('correoelectronico')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.email && <small className="text-danger ">{errors?.email.message}</small>}
            {/* {infoErrors?.email && <small className="text-danger ">{infoErrors?.email}</small>} */}
          </Col>
        </Row>
        <Row className="mt-4">
          {!isEditing && <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="password"
              control={control}
              rules={{
                minLength: { value: 9, message: t('caracteresminimos') },
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <div className='w-100'>
                  <CustomInput
                    type={showPassword ? 'text' : 'password'}
                    label={t('password')}
                    placeholder={t('password')}
                    value={value}
                    onChange={(e) => onChange(e)}
                    icon={
                      showPassword ? (
                        <FaEyeSlash
                          onClick={() => setShowPassword(false)}
                          color="black"
                          className="icon-password"
                        />
                      ) : (
                        <FaEye
                          onClick={() => setShowPassword(true)}
                          color="black"
                          className="icon-password"
                        />
                      )
                    }
                  />
                </div>
              )}
            />
            {errors.password && <small className="text-danger ">{errors.password.message}</small>}
          </Col>}
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="username"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  value={value}
                  label={t('username')}
                  onChange={(e) => onChange(e)}
                  placeholder={t('username')}
                />
              )}
            />
            {errors?.username && <small className="text-danger ">{errors.username.message}</small>}
            {/* {infoErrors?.username && <small className="text-danger ">{infoErrors?.username}</small>} */}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="is_active"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomCheckbox
                  label={t('activo')}
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="profile"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={choices}
                  label={t('tipousuario')}
                  onCheckedItem={(e) => onChange(e)}
                  placeholder={t('select')}
                  value={value}
                />
              )}
            />
            {errors?.profile && <small className="text-danger ">{errors.profile.message}</small>}
          </Col>
        </Row>
        <Row >
          {typeValue === 'worker' && (<>
            <Col sm={4} className='mt-4'>
              <Controller
                name="job"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    value={value}
                    label={t('job')}
                    onChange={(e) => onChange(e)}
                    placeholder={t('job')} />
                )}
              />
            </Col>
            <Col sm={2} className='mt-4'>
              <Controller
                name="supervisor"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomCheckbox
                    label={t('supervisor')}
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            </Col>
            <Col sm={2} className='mt-4'>
              <Controller
                name="is_administrator"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomCheckbox
                    label={t('administrator')}
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            </Col>
          </>)}
          {typeValue === 'commercial' && (<>
            <Col sm={4} className='mt-4'>
              <Controller
                name="margin_library"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="number"
                    min={0}
                    step='any'
                    value={value}
                    label={t('margin_library')}
                    onChange={(e) => onChange(e)}
                    placeholder={t('margin_library')} />
                )}
              />
            </Col>
            <Col sm={4} className='mt-4'>
              <Controller
                name="margin_bookstore"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="number"
                    min={0}
                    step='any'
                    value={value}
                    label={t('margin_bookstore')}
                    onChange={(e) => onChange(e)}
                    placeholder={t('margin_bookstore')} />
                )}
              />
            </Col>
          </>)}
          {typeValue === 'normal' && (<>
            <Col sm={4} className='mt-3'>
              <Controller
                name="date_birth"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('camporequerido'),
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t('date_birth')}
                    className="input-control w-100 form-control"
                    value={value}
                    onChange={onChange}
                    dateFormat="yyyy-MM-dd"
                    showTimeInput={false}
                    placeholderText={t('date_birth')}
                    showIcon={true}
                    icon={<BiCalendarCheck />}
                  />
                )}
              />
              {errors?.date_birth && <small className="text-danger ">{errors.date_birth.message}</small>}
            </Col>
            <Col sm={4} className='mt-4'>
              <Controller
                name="dni"
                control={control}
                rules={{
                  maxLength: {
                    value: 10,
                    message: t('max10characters'),
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    value={value}
                    label='DNI'
                    onChange={(e) => onChange(e)}
                    placeholder='DNI' />
                )}
              />
              {errors?.dni && <small className="text-danger ">{errors.dni.message}</small>}
            </Col>
            <Col sm={4} className='mt-4'>
              <Controller
                name="phone_number"
                control={control}
                rules={{
                  pattern: {
                    value: /^[0-9\b]+$/,
                    message: t('solonumeros'),
                  },
                  maxLength: {
                    value: 9,
                    message: t('max9characters'),
                  },
                  minLength: {
                    value: 9,
                    message: t('min9characters'),
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="tel"
                    label={t('telefono')}
                    onChange={(e) => onChange(e)}
                    value={value}
                    placeholder={t('telefono')}
                  />
                )}
              />
              {errors?.phone_number && <small className="text-danger">{errors.phone_number.message}</small>}
            </Col>
          </>)}
          {typeValue === 'library' && (
            <Col md={4} className='mt-4' style={{ display: 'none' }}>
              <Controller
                name="benefited_bookstore"
                control={control}
                render={({ field: { onChange, value } }) => {
                  const defaultCompanyValue = value?.value && value?.label
                    ? { id: value.value, fiscal_name: value.label, name: value.label }
                    : null;
                  return (
                    <div className='w-100 orders'>
                      <SearchCompanyInput
                        label={t('benefitedbookstore')}
                        onSelected={(item) => onChange(item)}
                        defaultCompany={defaultCompanyValue}
                        hasLabel={true}
                        placeholder={t('searchByBookstore')}
                        className="orders m-0"
                      />
                    </div>
                  );
                }}
              />
            </Col>
          )}
        </Row>
      </Form>
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button
          color="primary"
          onClick={handleSubmit(onSubmitForm)}
          loading={loading}
        >
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
        {isEditing &&
          <>
            {userPermissions.includes('user.change_user') && isWorker &&
              <Button
                type="button"
                color="terciary"
                onClick={() => setModalChangePassword(true)}
              >
                {t('cambiarcontrasena')}
              </Button>
            }
            <Button
              color="secondary"
              onClick={() => setOpenModalDelete(true)}
              loading={loading}
            >
              {t('eliminar')}
            </Button>
          </>}
      </div>
      <ChangePassword
        show={modalChangePassword}
        setShow={setModalChangePassword}
        id={id}
        beforeTitle={t('edit', { title: defaultValues?.full_name })}
      />
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deleteuser')}
        beforeTitle={t('edit', { title: defaultValues?.full_name })}
      />
    </div>
  );
};

UserForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  id: PropTypes.any,
  loading: PropTypes.bool,
  errorUsername: PropTypes.string
};

export default UserForm;